import { type FC } from "react";
import { CheckCircle, Lock } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogDescription,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import documentIllustration from "@/shared/assets/illustrations/document.svg";

interface UploadLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  accepted: File[];
  blocked: File[];
  onUpgradeAll: () => void;
  onUploadAccepted: (files: File[]) => void;
  isUploading?: boolean;
}

export const UploadLimitModal: FC<UploadLimitModalProps> = ({
  isOpen,
  onClose,
  accepted,
  blocked,
  onUpgradeAll,
  onUploadAccepted,
  isUploading,
}) => {
  const totalFiles = accepted.length + blocked.length;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent size="sm">
        <DialogHeader>
          <div className="flex flex-col items-center gap-3 pt-2">
            <img src={documentIllustration} alt="Upload documents" className="h-24 w-24" />
            <DialogTitle className="text-center">
              Your free plan fits {accepted.length} more document{accepted.length === 1 ? "" : "s"}
            </DialogTitle>
            <DialogDescription className="text-center">
              Upgrade to upload all {totalFiles} at once.
            </DialogDescription>
          </div>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-4">
          {accepted.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium">These will upload now ({accepted.length})</p>
              {accepted.map((file) => (
                <div key={file.name} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 shrink-0 text-green-600" />
                  <p className="text-sm truncate">{file.name}</p>
                </div>
              ))}
            </div>
          )}

          {blocked.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium">Requires upgrade ({blocked.length})</p>
              {blocked.map((file) => (
                <div key={file.name} className="flex items-center gap-2 text-tertiary">
                  <Lock className="h-4 w-4 shrink-0" />
                  <p className="text-sm truncate">{file.name}</p>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-col items-center gap-2 pt-2">
            <Button variant="brand" className="w-full" onClick={onUpgradeAll}>
              Upload all {totalFiles} documents
            </Button>
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => onUploadAccepted(accepted)}
              disabled={isUploading}
            >
              Upload {accepted.length} document{accepted.length === 1 ? "" : "s"} now
            </Button>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};
